import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  useCreateResource,
  useDeleteResource,
  useEditResource,
  usePaginatedResource,
  useResourceItem,
} from "./useCrudResource"
import {
  adjustEmployeeWallet,
  createEmployeeBill,
  createMyEmployeeBill,
  deleteEmployeeBill,
  getEmployeeBill,
  getEmployeeBills,
  getEmployeeWallets,
  getEmployeeWalletTransactions,
  getMyEmployeeBills,
  getMyEmployeeWallet,
  payEmployeeAdvance,
  payEmployeeBill,
  payEmployeeBillBulk,
  rejectEmployeeBill,
  updateEmployeeBill,
} from "api/employeeBillApi"

export const EMPLOYEE_BILL_QUERY_KEY = "employee-bills"
export const EMPLOYEE_WALLET_QUERY_KEY = "employee-wallets"

const invalidateEmployeeBillData = queryClient => {
  queryClient.invalidateQueries({ queryKey: [EMPLOYEE_BILL_QUERY_KEY] })
  queryClient.invalidateQueries({ queryKey: [EMPLOYEE_WALLET_QUERY_KEY] })
}

export const useEmployeeBills = (params = {}, enabled = true) =>
  usePaginatedResource(EMPLOYEE_BILL_QUERY_KEY, getEmployeeBills, params, enabled)

export const useMyEmployeeBills = (params = {}, enabled = true) =>
  usePaginatedResource(`${EMPLOYEE_BILL_QUERY_KEY}-mine`, getMyEmployeeBills, params, enabled)

export const useEmployeeBill = (id, enabled = true) =>
  useResourceItem(EMPLOYEE_BILL_QUERY_KEY, getEmployeeBill, id, enabled)

export const useCreateEmployeeBill = () =>
  useCreateResource(createEmployeeBill, EMPLOYEE_BILL_QUERY_KEY)

export const useCreateMyEmployeeBill = () =>
  useCreateResource(createMyEmployeeBill, `${EMPLOYEE_BILL_QUERY_KEY}-mine`)

export const useEditEmployeeBill = () =>
  useEditResource(updateEmployeeBill, EMPLOYEE_BILL_QUERY_KEY)

export const useDeleteEmployeeBill = () =>
  useDeleteResource(deleteEmployeeBill, EMPLOYEE_BILL_QUERY_KEY)

export const useRejectEmployeeBill = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: rejectEmployeeBill,
    onSuccess: () => invalidateEmployeeBillData(queryClient),
  })
}

export const usePayEmployeeBill = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: payEmployeeBill,
    onSuccess: () => invalidateEmployeeBillData(queryClient),
  })
}

export const usePayEmployeeBillBulk = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: payEmployeeBillBulk,
    onSuccess: () => invalidateEmployeeBillData(queryClient),
  })
}

export const useEmployeeWallets = (params = {}, enabled = true) =>
  usePaginatedResource(EMPLOYEE_WALLET_QUERY_KEY, getEmployeeWallets, params, enabled)

export const useMyEmployeeWallet = (enabled = true) =>
  useQuery({
    queryKey: [EMPLOYEE_WALLET_QUERY_KEY, "mine"],
    queryFn: getMyEmployeeWallet,
    enabled,
  })

export const useEmployeeWalletTransactions = (employeeId, params = {}, enabled = true) =>
  usePaginatedResource(
    `${EMPLOYEE_WALLET_QUERY_KEY}-transactions`,
    qs => getEmployeeWalletTransactions({ employeeId, ...qs }),
    params,
    enabled && !!employeeId
  )

export const usePayEmployeeAdvance = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: payEmployeeAdvance,
    onSuccess: () => invalidateEmployeeBillData(queryClient),
  })
}

export const useAdjustEmployeeWallet = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: adjustEmployeeWallet,
    onSuccess: () => invalidateEmployeeBillData(queryClient),
  })
}
